import { Components, Data, FileUtils, NeoModel } from '@singularsystems/neo-core';
import { Views } from '@singularsystems/neo-react';
import TradeRequestCriteria from '../Models/Trading/Criteria/TradeRequestCriteria';
import TradeRequestLookup from '../Models/Trading/Lookups/TradeRequestLookup';
import { AppService, Types } from '../TransactionsTypes';
import { PendingTradeRequestsComponentModel } from './Components/TradeRequests/PendingTradeRequestsComponentModel';
import { CompletedTradesCriteria } from 'st-reporting/Models/TradesCriteria';
import AppDataLookup from '../Models/Portfolio/AppDataLookup';

@NeoModel
export default class TradeHistoryVM extends Views.ViewModelBase {

    constructor(
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        private notifications = AppService.get(Types.Neo.UI.GlobalNotifications),
        private portfolioService = AppService.get(Types.Transactions.Services.PortfolioService),
        private apiClient = AppService.get(Types.Transactions.ApiClients.PortfolioApiClient),
        public awardsDataCache = AppService.get(Types.Awards.Services.AwardsDataCache),
        public authService = AppService.get(Types.Shared.Services.STAuthenticationService),
        public reportingApiClient = AppService.get(Types.Reporting.ApiClients.ReportingApiClient),) {

        super(taskRunner);

        this.criteria.activeTrades = false;

        this.autoDispose(this.criteria.onAnyPropertyChanged(() => this.pageManager.refreshData()));
    }

    public criteria = new TradeRequestCriteria();
    public pageManager = new Data.PageManager(this.criteria, TradeRequestLookup, this.apiClient.getTradeRequestsPagedAsync, {
        initialTaskRunner: this.taskRunner,
        pageSize: 10,
        sortBy: "tradeRequestId",
        sortAscending: false,
        afterFetch: this.afterFetch.bind(this)
    });

    public tradeRequestVM = new PendingTradeRequestsComponentModel(this.taskRunner);

    public portfolioData: AppDataLookup | null = null;

    public hasCostsAlreadyPaidGroups: boolean = false;

    public completedTradesCriteria: CompletedTradesCriteria | null = null;

    public async initialise() {
        this.portfolioData = await this.portfolioService.ensureLoaded(this.taskRunner, { allowNullCurrency: true, fetchPendingTrades: true }, Components.ErrorDisplayType.ThrowError);
        const incentiveSchemes = await this.awardsDataCache.incentiveSchemes.getDataAsync();
        this.hasCostsAlreadyPaidGroups = this.portfolioService.appData?.hasCostsAlreadyPaidGroups ?? false;

        this.pageManager.refreshData();
        this.pageManager.data.forEach(trade => {
            const incentiveScheme = incentiveSchemes.find(s => s.incentiveSchemeId === trade.incentiveSchemeId);
            trade.showUnitColumns = incentiveScheme?.showUnitColumns ?? true;
            trade.showPriceColumns = incentiveScheme?.showPriceColumns ?? true;
            trade.showAwardCostsColumn = incentiveScheme?.showAwardCostsColumn ?? true;
        });
    }

    public get showTradeDetailsButton() {
        return this.portfolioService.appData?.settings.showParticipantTradeDetailsButton;
    }

    private afterFetch(trades: TradeRequestLookup[]) {
        if (this.portfolioService.appData) {
            this.portfolioService.appData.populateTradeRequests(trades);
        }
    }

    public async downloadCompletedTradesPdf() {
        this.completedTradesCriteria!.participantId = this.authService.user!.participantId

        this.taskRunner.run(async () => {
            try {
                const blobData = await this.reportingApiClient.getCompletedTradesPdfAsync(this.completedTradesCriteria!.toQueryObject());
                if (blobData) {
                    FileUtils.showSaveFile(blobData);
                } else {
                    this.notifications.addDanger("Error", "Failed to download the PDF. Please try again later.");
                }
            } catch (error) {
                this.notifications.addDanger("Error", "An unexpected error occurred while downloading the PDF. Please contact support if the issue persists.");
            }
        });

        this.completedTradesCriteria = null;
    }
}
